import { HttpErrorResponse, HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { apiErrorBody } from '../api';
import { CONSTANTS_ERRORS } from '../common/constants/errors/errors.constants';
import { CONSTANTS_ROUTING } from '../common/constants/routing/routing.constants';
import { IAdvertisement } from '../common/interfaces/advertisement.interface';
import { LayoutService } from '../core/providers/layout/layout.service';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isAdvertisement = (value: unknown): value is IAdvertisement =>
  isRecord(value) && typeof value['name'] === 'string';

const stringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);

export const surveyInterceptor: HttpInterceptorFn = (req, next) => {
  const layoutService = inject(LayoutService);
  const router = inject(Router);
  const zone = inject(NgZone);

  const applyVenueSettings = (source: Record<string, unknown>): void => {
    const { advertisement, branding } = source;

    if (isAdvertisement(advertisement)) {
      layoutService.setAdvertisementURL(advertisement);
    }
    if (isRecord(branding)) {
      layoutService.setVenueLogo(stringOrNull(branding['logoFileUuid']));
      layoutService.setLayoutDefaultColor(stringOrNull(branding['colorTint']));
    }
  };

  const setGeneralSettings = (body: unknown): void => {
    if (!isRecord(body)) {
      return;
    }
    applyVenueSettings(body);
    // The campaign endpoints nest the venue settings under `info`.
    if (isRecord(body['info'])) {
      applyVenueSettings(body['info']);
    }
  };

  const handleErrors = (error: unknown): void => {
    if (apiErrorBody(error)?.errorCode === CONSTANTS_ERRORS.ERR_2403) {
      zone.run(() => void router.navigate([CONSTANTS_ROUTING.ROUTES.VENUE_DISABLED]));
    }
  };

  return next(req).pipe(
    tap(event => {
      if (event.type === HttpEventType.Response) {
        setGeneralSettings(event.body);
      }
    }),
    catchError((error: unknown) => {
      handleErrors(error);
      // An expired survey answers 410 with the venue's marketing data, branding included.
      if (error instanceof HttpErrorResponse) {
        setGeneralSettings(error.error);
      }

      return throwError(() => error);
    })
  );
};
